import { Root } from '@radix-ui/react-aspect-ratio';
import { ResponsiveImage } from '@/components/elements/ResponsiveImage';
import Container from '@/components/global/Container';
import { BookingWidget } from '@/components/shared/BookingSearchBar/BookingWidget';
import { IComponentSharedHeroImage, IEnum_Componentsharedheroimage_Variant as Variant } from '@/graphql/__generated__/global/types';
import classNames from '@/lib/classNames';
import { getImageUrl } from '@/lib/getImageUrl';
import { cubicBezier, m as motion } from 'framer-motion';
import Image from 'next/image';

const imageParentContainerStyles: Record<Variant, string> = {
    landing: 'brightness-75',
    detail: 'relative rounded-b-3xl',
};

const textGridStyles: Record<Variant, string> = {
    landing: 'absolute text-white flex items-center justify-end bottom-[10%] left-0 right-0 max-w-lg w-full mx-auto px-6',
    detail: 'py-8 text-primary lg:flex-row lg:text-left flex-wrap gap-2 justify-between',
};

const headingStyles: Record<Variant, string> = {
    landing: '',
    detail: 'lg:w-3/5',
};

const descriptionStyles: Record<Variant, string> = {
    landing: 'lg:w-8/12 xl:w-6/12',
    detail: 'w-full lg:w-1/3',
};

const HeroContent = ({ variant, title, description, descriptionHighlight }: { variant: Variant; title: string; description?: string; descriptionHighlight?: string }) => (
    <div className={classNames('w-full flex flex-col text-center', textGridStyles[variant as Variant])}>
        <motion.h1
            className={classNames('w-full', headingStyles[variant as Variant])}
            initial={{ y: 10, opacity: 0.1 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
                duration: 0.5,
                delay: 0.5,
                ease: cubicBezier(0.35, 0.17, 0.3, 0.86),
            }}
        >
            {title}
        </motion.h1>

        {(description || descriptionHighlight) && (
            <motion.div
                className={descriptionStyles[variant as Variant]}
                initial={{ y: 20, opacity: 0.1 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                    duration: 0.75,
                    delay: 0.75,
                    ease: cubicBezier(0.35, 0.17, 0.3, 0.86),
                }}
            >
                {description && <p className={classNames(descriptionHighlight && description.length > 50 ? 'small' : 'large')}>{description}</p>}
                <br />
                {descriptionHighlight && <p className="h5 whitespace-pre-wrap">{descriptionHighlight}</p>}
            </motion.div>
        )}
    </div>
);
export const HeroImage = ({
    image,
    variant,
    descriptionHighlight,
    showBookingBar,
    bookingBarText,
    title,
    description,
}: IComponentSharedHeroImage & {
    title: string;
    description?: string;
    showBookingBar?: boolean;
    bookingBarText?: string;
}) => {
    const imageUrl = getImageUrl(image);

    return (
        <section
            className={classNames(
                variant === Variant.Landing && showBookingBar && 'h-[100vh] lg:h-[90vh] min-h-heroFullScreen lg:min-h-heroFullScreen90vh mb-16',
                variant === Variant.Landing && !showBookingBar && 'h-[100vh] min-h-heroFullScreen show-page-wrapper',
                `relative flex flex-col`
            )}
        >
            <div className={classNames('w-full h-full overflow-hidden absolute min-h-hero', imageParentContainerStyles[variant as Variant])}>
                {variant === Variant.Detail && (
                    // @todo reverted responsive image component
                    <Root ratio={16 / 6} className="min-h-hero">
                        <Image className="w-full h-full object-cover" src={imageUrl as string} fill alt={image.data?.attributes?.alternativeText ?? ''} priority />
                    </Root>
                )}
                {variant === Variant.Landing && <ResponsiveImage alt={image.data?.attributes?.alternativeText ?? ''} aspectRatio={1728 / 1090} src={imageUrl} priority />}
                {showBookingBar && variant === Variant.Detail && (
                    <div className={classNames('absolute w-full bottom-0 lg:-bottom-24 left-0')}>
                        <BookingWidget bookingBarText={bookingBarText} showMobileBookingBar={true} />
                    </div>
                )}
            </div>
            {variant === Variant.Detail && (
                <Container>
                    <HeroContent variant={variant} title={title ? title : ''} description={description} descriptionHighlight={descriptionHighlight} />
                </Container>
            )}
            {variant === Variant.Landing && ( // @todo fix to make landing hero in container (quick fix before prod deploy)
                <HeroContent variant={variant} title={title} description={description} descriptionHighlight={descriptionHighlight} />
            )}

            {showBookingBar && variant === Variant.Landing && (
                <div className={classNames('absolute w-full -bottom-8 lg:-bottom-48 left-0')}>
                    <BookingWidget bookingBarText={bookingBarText} showMobileBookingBar={true} />
                </div>
            )}
        </section>
    );
};

export default HeroImage;
